import { useEffect, useState } from 'react';
import { videoSessionService, VideoSession } from '@/services/videoSession.service';
import { VideoSessionStatus } from '@/shared/enums/VideoSessionStatus';

interface UseVideoSessionStatusOptions {
  enabled?: boolean;
}

interface UseVideoSessionStatusResult {
  hasCompletedVideoCall: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook para verificar se há sessões de vídeo completadas em uma inspeção
 * @param items - Array de items da inspeção (com id)
 * @param options - Opções do hook (enabled para controlar quando verificar)
 * @returns Objeto com hasCompletedVideoCall, isLoading e error
 */
export function useVideoSessionStatus(
  items: Array<{ id?: string }> | undefined,
  options: UseVideoSessionStatusOptions = {}
): UseVideoSessionStatusResult {
  const { enabled = true } = options;
  const [hasCompletedVideoCall, setHasCompletedVideoCall] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkCompletedVideoCall = async () => {
      if (!enabled) {
        return;
      }

      if (!items || items.length === 0) {
        setHasCompletedVideoCall(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        for (const item of items) {
          if (!item.id) continue;

          try {
            const sessions = await videoSessionService.getVideoSessionsByInspectionItemId(item.id);

            if (sessions && Array.isArray(sessions)) {
              const hasCompleted = sessions.some(
                (session: VideoSession) =>
                  session.status === VideoSessionStatus.Completed && session.actualEndTime
              );

              if (hasCompleted) {
                setHasCompletedVideoCall(true);
                setIsLoading(false);
                return;
              }
            }
          } catch (err) {
            // Ignorar erro 404 (quando não há sessões)
            if ((err as any)?.response?.status !== 404) {
              setError(err as Error);
            }
          }
        }
        setHasCompletedVideoCall(false);
      } catch (err) {
        setError(err as Error);
        setHasCompletedVideoCall(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkCompletedVideoCall();
  }, [items, enabled]);

  return {
    hasCompletedVideoCall,
    isLoading,
    error,
  };
}

